<template>
    <div class="RiskProfileCriteriaTable flex flex-col gap-2.5 w-full">
        <RiskProfileCriteriaTableRow
            v-for="(criterion, index) of criteriaList"
            :key="index"
            :only-row="criteriaList.length === 1"
            :criterion="criterion"
            :template="templatesByFieldName[criterion.field]"
            :some-custom-property-substitution="someCustomPropertySubstitution"
            :criteria-can-custom-property-substitution="criteriaCanCustomPropertySubstitution"
            :custom-properties="customProperties"
            :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
            :show-validation-errors="showValidationErrors"
            :can-manage-criteria="canManageCriteria"
            :can-open-settings="canOpenSettings"
            show-error
            @delete="onRowDelete(index)"
            @input="onRowInput($event, index)"
            @open-settings="$emit('openSettings', $event)"
            @reference-accessed="$emit('reference-accessed', $event)"
            @validity-change="$emit('validityChange', $event)"
        />
    </div>
</template>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import {
        JsonSchemaBase,
        JsonSchemaComplexObject,
        JsonSchemaObject,
    } from '@evidentid/json-schema/interfaces/JsonSchema';
    import {
        TprmSchemaDisplayFormat,
    } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';

    import NewButton from '@/components/new-button/NewButton.vue';
    import {
        IsReferenceStartsWithIsInvalid,
    } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import RiskProfileCriteriaTableRow
        from '@/modules/decisioning-criteria/components/RiskProfileCriteriaTableRow/RiskProfileCriteriaTableRow.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { filterCustomPropertiesByValueType } from '@/modules/decisioning-criteria/utils/filterCustomPropertiesByValueType';
    import {
        getFlagDisabledReferenceValueType,
    } from '@/modules/decisioning-criteria/utils/getFlagDisabledReferenceValueType';

    import { CriteriaCanCustomPropertySubstitution } from './types';

    @Component({
        emits: [ 'input', 'openSettings', 'reference-accessed', 'validityChange' ],
        components: {
            NewButton, HeadCell, Table, RiskProfileCriteriaTableRow,
        },
    })
    export default class RiskProfileCriteriaTable extends Vue {
        @Prop({ type: Array, default: () => [] })
        private criteriaList!: EditableRequirementCriterion[];

        @Prop({ type: Object, default: () => ({}) })
        private templatesByFieldName!: Record<string, CriterionTemplate>;

        @Prop({ type: Array, default: () => [] })
        private customProperties!: CustomProperty[];

        @Prop({ type: Boolean, default: false })
        private showValidationErrors!: boolean;

        @Prop({ type: Function as PropType<IsReferenceStartsWithIsInvalid>, required: true })
        private isReferenceStartsWithIsInvalid!: IsReferenceStartsWithIsInvalid;

        @Prop({ type: Boolean, default: true })
        private canManageCriteria!: boolean;

        @Prop({ type: Boolean, default: true })
        private canOpenSettings!: boolean;

        private get criteriaCanCustomPropertySubstitution(): CriteriaCanCustomPropertySubstitution {
            return this.criteriaList.reduce((result, criterion) => {
                if (this.canCriterionCustomPropertySubstitution(criterion)) {
                    result[criterion.field] = true;
                }
                return result;
            }, {} as CriteriaCanCustomPropertySubstitution);
        }

        private get someCustomPropertySubstitution(): boolean {
            return Object.keys(this.criteriaCanCustomPropertySubstitution).length > 0;
        }

        private canCriterionCustomPropertySubstitution(
            criterion: EditableRequirementCriterion,
        ): boolean {
            const template = this.templatesByFieldName[criterion.field];

            if (!template) {
                return false;
            }

            const conditionalRefs = template.displayMetadata.conditionalValueReference;
            const requiredValueRef = template.displayMetadata.requiredValueReference;
            const isValueAndCustomPropertySchemaCriterion = Boolean(
                requiredValueRef && (
                    template.evaluator.references[requiredValueRef]?.schema as JsonSchemaObject
                )?.properties?.isBasedOnInsuredField,
            );
            let customPropertySchema = null;
            let valueSchema = null;
            if (conditionalRefs) {
                customPropertySchema = template.evaluator.references[conditionalRefs.flagEnabledReference]?.schema;
            } else if (isValueAndCustomPropertySchemaCriterion) {
                // BE starting to use valueAndInsuredField schema which will always be
                // if => isBasedOnInsuredField, then => insuredField, else => value
                customPropertySchema = (
                    template.evaluator.references[requiredValueRef as string]?.schema as JsonSchemaComplexObject
                )?.then?.properties?.insuredField;
                valueSchema = (
                    template.evaluator.references[requiredValueRef as string]?.schema as JsonSchemaComplexObject
                )?.else?.properties?.value;
            } else {
                return false;
            }

            if (customPropertySchema &&
                'displayFormat' in customPropertySchema &&
                customPropertySchema.displayFormat !== TprmSchemaDisplayFormat.insuredField
            ) {
                return false;
            }

            const type = conditionalRefs
                ? getFlagDisabledReferenceValueType({ template })
                : (valueSchema as JsonSchemaBase)?.type;

            return filterCustomPropertiesByValueType(
                this.customProperties,
                type,
            ).length > 0;
        }

        private onRowDelete(index: number): void {
            this.$emit('input', [
                ...this.criteriaList.slice(0, index),
                ...this.criteriaList.slice(index + 1),
            ]);
        }

        private onRowInput(
            criterion: EditableRequirementCriterion,
            index: number,
        ): void {
            this.$emit('input', [
                ...this.criteriaList.slice(0, index),
                criterion,
                ...this.criteriaList.slice(index + 1),
            ]);
        }
    }
</script>
